import React, { useEffect, useState } from 'react';

import ImpactReport from './ImpactReport';
import { confirmDestructive } from '../common/confirmDestructive';
import { getTagReferences } from '../../services/tagReferences';
import { cleanupFor } from '../../utils/itemTagSettings';
import { isPrivateTag, tagIdOf } from '../../utils/tags';

/**
 * The question before a tag is deleted. Resolves `false` not to delete, or
 * `{ cleanup }` to delete — `cleanup` being the "Remove for me" choices the
 * DELETE carries (`[{ holder, id }]`, maybe empty; services/tags.js
 * deleteTag).
 *
 * The platform's destructive confirm (red Delete, Cancel focused): what goes
 * with the tag and whether it comes back — and, first, THE IMPACT REPORT
 * (ruled d33, d38; T4d): what refers to the tag (`GET /api/tags/{id}/
 * references`), each entry with its link and **Remove for me** / **Leave
 * it** (components/tags/ImpactReport.js), and the sentence that other
 * people's pins and filters stop matching too (d37). Delete waits until the
 * report has answered; a report that could not be read says so and lets the
 * delete go ahead (⚑ T4d: a failed report never blocks a manager's delete —
 * the refusals that matter are the API's own). Every delete in the tag
 * managers comes through here.
 */
function TagDeleteBody({ tag, onChoices, onReady }) {
	const tagId = tagIdOf(tag);
	const privateTag = isPrivateTag(tag);
	const [report, setReport] = useState({ loading: true, error: null, references: [], truncated: false });
	const [choices, setChoices] = useState({});
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let live = true;
		if (attempt) setReport({ loading: true, error: null, references: [], truncated: false });
		getTagReferences(tagId)
			.then(answer => { if (live) setReport({ loading: false, error: null, ...answer }); })
			.catch(error => { if (live) setReport({ loading: false, error, references: [], truncated: false }); })
			.finally(() => { if (live) onReady?.(); });
		return () => { live = false; };
	}, [tagId, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

	const choose = (key, value) => setChoices(prev => {
		const next = { ...prev, [key]: value };
		onChoices?.(cleanupFor(report.references, next));
		return next;
	});

	return (
		<>
			{privateTag ? (
				<p>
					It comes off everything you put it on. You can restore it from <strong>Deleted tags</strong>,
					but not the places it was on.
				</p>
			) : (
				<p>
					It comes off every item that carries it, for everyone. You can restore it from{' '}
					<strong>Deleted tags</strong>, and it goes back on those items.
				</p>
			)}
			<ImpactReport
				references={report.references}
				truncated={report.truncated}
				loading={report.loading}
				error={report.error}
				onRetry={() => setAttempt(n => n + 1)}
				choices={choices}
				onChange={choose}
				subject="this tag"
			/>
		</>
	);
}

export default function confirmTagDelete(modal, tag) {
	return new Promise(resolve => {
		let cleanup = [];
		let dialog = null;
		let ready = false;
		dialog = confirmDestructive(modal, {
			title: `Delete “${tag?.name || 'this tag'}”?`,
			width: 560,
			content: (
				<TagDeleteBody
					tag={tag}
					onChoices={next => { cleanup = next; }}
					onReady={() => {
						ready = true;
						dialog?.update?.({ okButtonProps: { danger: true, disabled: false } });
					}}
				/>
			),
			okText: 'Delete',
			// The report comes first: Delete waits for it (confirmDestructive
			// keeps the red; this adds the wait).
			okButtonProps: { disabled: !ready },
			onOk: () => resolve({ cleanup }),
			onCancel: () => resolve(false),
		});
		if (ready) dialog?.update?.({ okButtonProps: { danger: true, disabled: false } });
	});
}
